// Taktik, Ansicht: Brett mit Leisten, rechts Wertung, Thema, letzte Ergebnisse und der Stand des Rätsels.
import { h, type VNode } from 'snabbdom';
import { board } from '../board';
import { t } from '../i18n';
import type { PuzzleCtrl } from '../puzzlectrl';
import { THEMES, themeName } from '../puzzlethemes';
import { playerBar } from './playerbar';

const btn = (label: string, on: () => void, cls = '') => h(`button.btn${cls}`, { on: { click: on } }, t(label));
const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '±0');

function head(c: PuzzleCtrl): VNode {
  const counts = c.counts;
  const num = (n: number) => n.toLocaleString(document.documentElement.lang || 'de');
  return h('div.setup', [
    h('div.hd', h('div.row', [h('h3', t('Deine Rätsel-Wertung')), h('span.score', String(c.rating.rating))])),
    h('div.fld', h('div.row', [
      h('label', { attrs: { for: 'puzzle-theme' } }, t('Thema')),
      h('select#puzzle-theme', { on: { change: (e: Event) => c.setTheme((e.target as HTMLSelectElement).value) } }, [
        h('option', { attrs: { value: '' }, props: { selected: !c.theme } }, t('Alle Themen')),
        ...THEMES.map(([k, name]) => h('option', { attrs: { value: k }, props: { selected: c.theme === k } },
          counts[k] ? `${t(name)} (${num(counts[k])})` : t(name))),
      ]),
    ])),
    c.rating.history.length ? h('div.fld', h('div.results', c.rating.history.map(r =>
      h(`span.res.${r.win ? 'win' : 'loss'}`, { attrs: { title: t('Rätsel-Wertung {r}', { r: r.rating }) } }, signed(r.delta))))) : null,
  ]);
}

function status(c: PuzzleCtrl): (VNode | null)[] {
  const side = c.color === 'white' ? t('Weiß') : t('Schwarz');
  const tools = h('div.row.btns', [btn('Hinweis', () => c.hint()), btn('Lösung zeigen', () => void c.reveal())]);
  const r = c.result, p = c.puzzle;
  const done = () => [
    r ? h('p.small', r.win || r.delta === 0 ? t('Deine Wertung: {d}', { d: signed(r.delta) }) : t('Deine Wertung: {d} (der erste Versuch zählt)', { d: signed(r.delta) })) : null,
    p ? h('p.muted.small', [t('Rätsel-Wertung {r}', { r: p.rating }), ...p.themes.map(themeName).filter(Boolean).map(n => ' · ' + t(n!))].join('')) : null,
    h('div.row.btns', [btn('Nächstes Rätsel', () => void c.next(), '.accent')]),
  ];
  switch (c.state) {
    case 'loading':
      return [h('p.muted.small', t('Rätsel wird geladen …'))];
    case 'error':
      return [h('p.error', c.error), h('div.row.btns', [btn('Nochmal laden', () => void c.next())])];
    case 'wrong':
      return [h('p.warn', t('Das ist nicht die Lösung. Noch ein Versuch?')), tools];
    case 'solved':
      return [h('p.ok', t('Gelöst!')), ...done()];
    case 'shown':
      return [h('p', t('Lösung gezeigt.')), ...done()];
    default:
      return [h('p', c.pos.turn === c.color ? t('Finde den besten Zug für {s}.', { s: side }) : t('Der Gegner zieht …')), tools];
  }
}

export function puzzleView(c: PuzzleCtrl): VNode {
  const other = c.color === 'white' ? 'black' : 'white';
  return h('section.play.puzzle', [
    h('div.play-body', [
      h('div.board-col', [
        playerBar('top', { color: other, name: t('Gegner'), active: c.pos.turn === other }),
        board('puzzle-board', {
          fen: c.fen,
          orientation: c.color,
          turnColor: c.pos.turn,
          check: c.pos.isCheck() ? c.pos.turn : false,
          lastMove: c.last,
          coordinates: true,
          animation: { enabled: true, duration: 250 },
          movable: { free: false, color: c.color, dests: c.dests(), showDests: true, events: { after: (o, d) => void c.move(o, d) } },
          premovable: { enabled: false },
          drawable: { enabled: true, visible: true, autoShapes: c.hintSquare ? [{ orig: c.hintSquare, brush: 'green' }] : [] },
        }),
        playerBar('bottom', { color: c.color, name: t('Du'), active: c.pos.turn === c.color, info: String(c.rating.rating) }),
      ]),
      h('div.side', [
        head(c),
        h('div.setup.coach', [h('div.hd', h('h3', t('Rätsel'))), h('div.fld', status(c))]),
        h('p.muted.small.source', t('Rätsel aus der lichess-Datenbank (gemeinfrei, CC0).')),
      ]),
    ]),
  ]);
}
